import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { EnvironmentService } from "src/service/environment.service";
import { NetworkService } from "src/service/network.service";
import { EnvironmentType } from "src/components/shared-components/enum/enumEnvironment";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { Store } from "@ngrx/store";
import { listGateway } from "src/state/selectors/gateway.selectors";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";

@Component({
    selector: "app-environment-search",
    templateUrl: "./environment-search.component.html",
    styleUrls: ["./environment-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

/**
 * Modal that lets the user search and pick an environment to associate.
 *
 * Dismiss contract (relied upon by every caller — the weekly-planning form and the
 * device-creation popup — and therefore MUST be preserved): on confirm with a selection it
 * dismisses with `{ id, name }` (name = the environment room label); on cancel, or confirm
 * with no selection, it dismisses with no data.
 */
export class EnvironmentSearchComponent
  implements OnInit, OnDestroy, iConnToolbarColour, iTranslation, iEvent {
  private _logContext: string = "Environment-search.component";

  private _environmentStoreSubscription: Subscription = null;
  private _gatewayStoreSubscription: Subscription = null;
  private _networkSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;
  private _searchSubscription: Subscription = null;
  private _searchSubject: Subject<string> = new Subject<string>();
  private _listGateway: Array<Gateway> = [];

  /** Number of rows added to the rendered window per page (initial view and each infinite-scroll). */
  private readonly _pageSize: number = 30;

  /**
   * Environments matching the current search text, materialized on each data/search change so the
   * (potentially heavy) filter runs only then instead of on every change-detection cycle.
   */
  private _matchingEnvironments: Environment[] = [];

  /** How many of {@link _matchingEnvironments} are currently rendered (grows via infinite scroll). */
  private _renderedCount: number = 0;

  public _eventSubscriptions: Array<Subscription> = null;

  public environmentList: Environment[] = [];
  public environmentSelected: Environment = null;

  /**
   * Optional environment list supplied by the caller (e.g. the weekly-planning form, which has
   * already fetched the current area's environments). When provided, it is used as-is instead of
   * issuing a redundant area-scoped API call; otherwise the list is fetched from the API.
   */
  @Input() environments: Environment[] = null;

  public searchText: string = "";
  public isLoading: boolean = true;
  public showErrorContent: boolean = false;

  /** True once every matching environment is rendered: disables the infinite scroll. */
  public allLoaded: boolean = false;

  /**
   * Height of one row, used to fill the loading list with just enough skeletons: mirrors
   * `.env-item` in the SCSS (`--min-height` plus its vertical margins).
   */
  public readonly skeletonRowHeight: number = 78;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  //#region Translated strings
  public noEnviromentFound: string = "";
  public searchEnv: string = "";
  public gatewayName: string = "";
  public nRoom: string = "";
  public description: string = "";
  //#endregion

  constructor(
    private _environmentService: EnvironmentService,
    private _modalCtrl: ModalController,
    private _networkService: NetworkService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _store: Store,
    private _eventService: EventService,
    private _loggerService: LoggerService
  ) { }

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subsribeNetworkServiceData();
    this.subscribeSearch();
    this.loadSelectableEnvironments();
    this.loadListGateway();
    this.subscribeEvents();
  }

  /**
   * Populate the picker either from the environment list supplied by the caller
   * ({@link environments}) - avoiding a redundant API call - or, when none is given, by fetching it
   * from the API (see {@link loadListEnvironmentFromApi}).
   */
  private loadSelectableEnvironments(): void {
    if (this.environments && this.environments.length > 0) {
      this.isLoading = false;
      this.showErrorContent = false;
      this.setEnvironmentList(
        this.filterSelectableEnvironments(this.environments)
      );
    } else {
      this.loadListEnvironmentFromApi();
    }
  }

  ngOnDestroy(): void {
    this._networkSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();
    this._environmentStoreSubscription?.unsubscribe();
    this._gatewayStoreSubscription?.unsubscribe();
    this._searchSubscription?.unsubscribe();
    this.unsubscribeEvents();
  }

  //#region Events
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (const sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  private subsribeNetworkServiceData(): void {
    this._networkSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;
          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /** The slice of matching environments currently rendered; grows one page at a time on scroll. */
  public get displayedEnvironments(): Environment[] {
    return this._matchingEnvironments.slice(0, this._renderedCount);
  }

  /** Total number of environments matching the current search (drives the empty state). */
  public get matchingCount(): number {
    return this._matchingEnvironments.length;
  }

  /**
   * Debounce the searchbar input so the list is re-filtered only after the user pauses typing,
   * instead of once per keystroke.
   */
  private subscribeSearch(): void {
    this._searchSubscription = this._searchSubject
      .pipe(debounceTime(150), distinctUntilChanged())
      .subscribe(() => {
        this.applySearch();
      });
  }

  /**
   * Handle searchbar input: keep the bound value and schedule a debounced re-filter.
   * @param event the ion-searchbar input event
   */
  public onSearchInput(event: any): void {
    this.searchText = event?.detail?.value ?? "";
    this._searchSubject.next(this.searchText);
  }

  /**
   * Recompute the environments matching the current search text (against the description, the room
   * label and the gateway name) and reset the rendered window to the first page. Runs on data load
   * and on each debounced search change — never per change-detection cycle.
   */
  private applySearch(): void {
    const query: string = this.searchText.trim().toLowerCase();

    this._matchingEnvironments = !query
      ? this.environmentList
      : this.environmentList.filter(
        (environment: Environment) =>
          (environment.descriptionEnvironment || "")
            .toLowerCase()
            .includes(query) ||
          (environment.nRoom || "").toLowerCase().includes(query) ||
          this.getGatewayName(environment.instanceGUID)
            .toLowerCase()
            .includes(query)
      );

    this._renderedCount = Math.min(
      this._pageSize,
      this._matchingEnvironments.length
    );
    this.allLoaded = this._renderedCount >= this._matchingEnvironments.length;
  }

  /**
   * Render the next page of matching environments when the user scrolls to the bottom.
   * @param event the ion-infinite-scroll event
   */
  public loadMore(event: any): void {
    this._renderedCount = Math.min(
      this._renderedCount + this._pageSize,
      this._matchingEnvironments.length
    );
    this.allLoaded = this._renderedCount >= this._matchingEnvironments.length;
    event.target.complete();
  }

  /**
   * `trackBy` for the results list: keeps existing rows stable (no DOM teardown) as the rendered
   * window grows via infinite scroll.
   * @param _index the row index (unused)
   * @param environment the row's environment
   * @returns the environment id used as identity
   */
  public trackByEnvironmentId(_index: number, environment: Environment): number {
    return environment.id;
  }

  /**
   * Mark the tapped environment as the (single) current selection.
   * @param environment the environment to select
   */
  public selectEnvironment(environment: Environment): void {
    this.environmentSelected = environment;
  }

  /**
   * @param environment the environment to test
   * @returns true when the given environment is the current selection
   */
  public isSelected(environment: Environment): boolean {
    return (
      this.environmentSelected != null &&
      this.environmentSelected.id === environment.id
    );
  }

  /** Confirm the selection and return it to the caller via the modal dismiss data. */
  public onClickConfirm(): void {
    if (this.environmentSelected) {
      this._modalCtrl.dismiss({
        id: this.environmentSelected.id,
        name: this.environmentSelected.nRoom,
      });
    } else {
      this._modalCtrl.dismiss();
    }
  }

  /** Close the modal without selecting anything. */
  public onclickCancel(): void {
    this._modalCtrl.dismiss();
  }

  /** Force a data reload after the "something went wrong" retry. */
  public forceReloadData(): void {
    this.isLoading = true;
    this.showErrorContent = false;
    this.loadListEnvironmentFromApi();
  }

  /**
   * Resolve the gateway description from its instance GUID.
   * @param instanceGUID the gateway instance GUID
   * @returns the gateway description, or empty string when unknown
   */
  public getGatewayName(instanceGUID: string): string {
    const currentGateway: Gateway = this._listGateway.find(
      (gateway: Gateway) => gateway.instanceGUID === instanceGUID
    );
    return currentGateway?.description || "";
  }

  /**
   * Load the selectable environments (common areas, rooms and customized environments) by fetching
   * them directly from the API, scoped to the current building's area (see {@link getEnvironments}),
   * with `extraData = false`. The shared environment store is deliberately bypassed: it is loaded
   * unfiltered (all buildings) and would surface environments outside the current building.
   */
  private loadListEnvironmentFromApi(): void {
    this.isLoading = true;
    this.showErrorContent = false;

    this._environmentStoreSubscription?.unsubscribe();
    this._environmentStoreSubscription = this._store
      .select(selectedBuilding)
      .pipe(
        take(1),
        map((building: Building) =>
          building ? building.idAreaLocal : null
        ),
        switchMap((idArea: number) =>
          this._environmentService.getEnvironments(idArea, null, false)
        )
      )
      .subscribe({
        next: (environmentObject: GenericApiData) => {
          this.isLoading = false;
          this.setEnvironmentList(
            this.filterSelectableEnvironments(environmentObject?.data || [])
          );
        },
        error: (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "loadListEnvironmentFromApi()",
            error
          );
          this.handleApiResponseError();
        },
      });
  }

  /**
   * Keep only the environment types the picker can offer (common areas, rooms and customized).
   * @param list the raw environment list
   * @returns the filtered, selectable environments
   */
  private filterSelectableEnvironments(
    list: Array<Environment>
  ): Array<Environment> {
    return (list || []).filter(
      (environment: Environment) =>
        environment.environmentType == EnvironmentType.COMMON_AREA ||
        environment.environmentType == EnvironmentType.ROOM ||
        environment.environmentType == EnvironmentType.CUSTOMIZED
    );
  }

  /**
   * Store the loaded environments and (re)build the searchable, paged view over them.
   * @param list the selectable environments to display
   */
  private setEnvironmentList(list: Array<Environment>): void {
    this.environmentList = list;
    this.applySearch();
  }

  /**
   * Load the gateways from the store; used to resolve gateway names for the rows.
   */
  private loadListGateway(): void {
    this._gatewayStoreSubscription = this._store
      .select(listGateway)
      .pipe(
        filter(
          (storeData: GenericStoreApiData) =>
            storeData !== null && storeData !== undefined
        ),
        filter((storeData: GenericStoreApiData) => storeData?.loaded),
        filter(
          (storeData: GenericStoreApiData) =>
            !(storeData.errorGeneric || storeData.errorNotYetImplemented)
        ),
        tap((storeData: GenericStoreApiData) => {
          if (storeData.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listGateway)",
              "loadListGateway()",
              "We are using old data!!"
            );
          }
        }),
        map((storeData: GenericStoreApiData) => storeData.data?.data || [])
      )
      .subscribe((list: Array<Gateway>) => {
        this._listGateway = list;
      });
  }

  private async handleApiResponseError(): Promise<void> {
    this.setEnvironmentList([]);
    this.isLoading = false;
    this.showErrorContent = true;
    await this._alertService.presentAlertSomethingWrong();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.noEnvironmentFound",
        "dictionary.searchEnvironmentAccess",
        "dictionary.gateway",
        "dictionary.nRoom",
        "dictionary.description",
      ])
      .subscribe((translation) => {
        this.noEnviromentFound = translation["dictionary.noEnvironmentFound"];
        this.searchEnv = translation["dictionary.searchEnvironmentAccess"];
        this.gatewayName = translation["dictionary.gateway"];
        this.nRoom = translation["dictionary.nRoom"];
        this.description = translation["dictionary.description"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion
}
